import React, { useState } from 'react';
import { usePageTitle } from '../hooks/usePageTitle';

// Functional component where you want to embed this form, e.g., App or a ContactPage component
export default function YourPageOrAppComponent() {
  usePageTitle({
    title: 'Contact Us',
    description: 'Get in touch with our team. We\'re here to help with your avatar generation needs.',
    image: '/img/contact-hero.jpg'
  });
  // State to manage form input values
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    category: '',
    message: '',
    productPhoto: null,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', null
  const [debugInfo, setDebugInfo] = useState(null);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  // Handle file upload
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (5MB = 5 * 1024 * 1024 bytes)
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        alert('File size must be less than 5MB');
        e.target.value = ''; // Clear the input
        return;
      }
      
      // Check file type (only images)
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        e.target.value = ''; // Clear the input
        return;
      }
      
      setFormData((prevData) => ({
        ...prevData,
        productPhoto: file,
      }));
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault(); // Prevent default browser form submission
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Get the Firebase function URL
      const functionUrl = import.meta.env.VITE_FIREBASE_FUNCTION_URL || 
                         'https://sendcontactemail-fvnbni43ua-uc.a.run.app';
      
      // Create FormData to handle file upload
      const submitData = new FormData();
      submitData.append('name', formData.name);
      submitData.append('email', formData.email);
      submitData.append('company', formData.company);
      submitData.append('category', formData.category);
      submitData.append('message', formData.message);
      
      if (formData.productPhoto) {
        submitData.append('productPhoto', formData.productPhoto);
      }
      
      const response = await fetch(functionUrl, {
        method: 'POST',
        body: submitData, // Don't set Content-Type header for FormData
      });

      const result = await response.json();

      if (result.success) {
        setSubmitStatus('success');
        setDebugInfo(null);
        console.log('✅ Contact form submitted successfully!', result);
        setFormData({
          name: '',
          email: '',
          company: '',
          category: '',
          message: '',
          productPhoto: null,
        });
        // Clear file input
        const fileInput = document.getElementById('productPhoto');
        if (fileInput) fileInput.value = '';
      } else {
        setSubmitStatus('error');
        setDebugInfo(result);
        console.error('Form submission error:', result.error);
        console.log('Debug info:', result);
      }
    } catch (error) {
      console.error('Network error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (

    <>
      <div className="py-4 py-lg-4 py-md-5"></div>
      <div className="py-4 py-lg-4 py-md-5"></div>
      <div className="container">
        <div className="row justify-content-start">
          <div className="col-12 col-md-10 offset-md-1">
            <h4 className="f-a f-uc">
              Get in Touch
            </h4>
          </div>
        </div>
      </div>

      <div className="container">
        <div className="row justify-content-start">
          <div className="col-12 col-md-6 offset-md-1 col-lg-7 offset-lg-1">
            <h6 className="f-b my-3">
              Getting started is easy
            </h6>
          </div>
        </div>
      </div>

      <div className="py-3 py-lg-3 py-md-3"></div>
      <div className="d-flex align-items-center justify-content-center min-vh-lg-100 b-c5 p-4 p-md-5"
        style={{
          backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='20' height='20'><circle cx='10' cy='10' r='1' fill='white' /></svg>\")",
          backgroundRepeat: 'repeat',
          backgroundSize: '20px 20px'
        }}
      >
        {/* Inner container: Sets max-width for the content and provides background/shadow/rounding */}
        <div className="container p-4 p-md-5 bg-white rounded shadow" style={{ maxWidth: '900px' }}>
          <div className="row g-4">
            <div className="order-lg-1 w-100">
              {submitStatus === 'success' && (
                <div className="alert alert-success mb-4">
                  <strong>Thank you!</strong> Your message has been sent successfully. We'll get back to you soon.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="alert alert-danger mb-4">
                  <strong>Oops!</strong> There was an error sending your message. Please try again or contact us directly.
                  
                  {debugInfo && (
                    <details className="mt-3">
                      <summary className="text-white">Debug Information (Click to expand)</summary>
                      <pre className="mt-2 text-white" style={{fontSize: '12px', whiteSpace: 'pre-wrap'}}>
                        {JSON.stringify(debugInfo, null, 2)}
                      </pre>
                    </details>
                  )}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <input
                    type="text"
                    name="name"
                    id="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="form-control f-b"
                    placeholder="Your Name"
                    required
                    disabled={isSubmitting}
                  />
                </div>

                <div className="mb-3">
                  <input
                    type="email"
                    name="email"
                    id="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="form-control f-b"
                    placeholder="Email"
                    required
                    disabled={isSubmitting}
                  />
                </div>

                <div className="mb-3">
                  <input
                    type="text"
                    name="company"
                    id="company"
                    value={formData.company}
                    onChange={handleChange}
                    className="form-control f-b"
                    placeholder="Company"
                    disabled={isSubmitting}
                  />
                </div>

                <div className="mb-3">
                  <select
                    name="category"
                    id="category"
                    value={formData.category}
                    onChange={handleChange}
                    className="form-control f-b"
                    required
                    disabled={isSubmitting}
                  >
                    <option value="" disabled>Category of Interest</option>
                    <option value="Compute">Compute</option>
                    <option value="Investor">Investor</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div className="mb-4">
                  <textarea
                    id="message"
                    name="message"
                    rows="5"
                    value={formData.message}
                    onChange={handleChange}
                    className="form-control f-b"
                    placeholder="Your message here"
                    required
                    disabled={isSubmitting}
                  ></textarea>
                </div>

                <div className="mb-4">
                  <div className="d-flex align-items-center">
                    <label htmlFor="productPhoto" className="btn b-c5 c-c0 me-3" style={{cursor: 'pointer'}}>
                      Upload Product Example
                    </label>
                    <span className="text-muted f-b">
                      {formData.productPhoto ? formData.productPhoto.name : 'Max. image size 5MB'}
                    </span>
                    <input
                      type="file"
                      name="productPhoto"
                      id="productPhoto"
                      onChange={handleFileChange}
                      className="d-none"
                      accept="image/*"
                      disabled={isSubmitting}
                    />
                  </div>
                  {formData.productPhoto && (
                    <div className="mt-2 text-muted small">
                      File size: {(formData.productPhoto.size / 1024 / 1024).toFixed(2)} MB
                    </div>
                  )}
                </div>

                <button
                  type="submit"
                  className="btn b-c5 c-c0 btn-lg w-100 shadow"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            </div>
          </div>
        </div>

      </div>
    </>
  );
}